import React, { useState } from "react";
import EmailIcon from "@material-ui/icons/Email";
import { LANDING_CONFIG } from "../../config/landingConfig";
import { LoadingButton } from "./LoadingStates";

const FormField = ({ field, value, onChange, error, isLoading }) => {
  const fieldId = `demo-${field.name}`;
  
  return (
    <div className={`form-field ${field.fullWidth ? 'full' : ''} ${isLoading ? 'loading' : ''}`}>
      <label htmlFor={fieldId} className="form-label">
        {field.label}
        {field.required && <span className="required-field" aria-label="required"> *</span>}
      </label>
      {field.type === 'textarea' ? (
        <textarea
          id={fieldId}
          name={field.name}
          value={value}
          onChange={onChange}
          className="form-textarea"
          placeholder={field.placeholder}
          disabled={isLoading}
          aria-describedby={error ? `${fieldId}-error` : undefined}
          aria-invalid={error ? 'true' : 'false'}
        />
      ) : (
        <input
          id={fieldId}
          type={field.type}
          name={field.name}
          value={value}
          onChange={onChange}
          className="form-input"
          placeholder={field.placeholder}
          disabled={isLoading}
          aria-describedby={error ? `${fieldId}-error` : undefined}
          aria-invalid={error ? 'true' : 'false'}
        />
      )}
      {error && (
        <div id={`${fieldId}-error`} className="error-message" role="alert">
          {error}
        </div>
      )}
    </div>
  );
};

const DemoSection = () => {
  const { demo, validation } = LANDING_CONFIG;
  const [formData, setFormData] = useState(
    demo.form.fields.reduce((acc, field) => {
      acc[field.name] = '';
      return acc;
    }, {})
  );
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    demo.form.fields.forEach(field => {
      const value = formData[field.name];
      
      if (field.required && !value.trim()) {
        newErrors[field.name] = validation.required;
      } else if (field.type === 'email' && value && !demo.form.validation.email.test(value)) {
        newErrors[field.name] = validation.email;
      } else if (field.type === 'tel' && value && !demo.form.validation.phone.test(value)) {
        newErrors[field.name] = validation.phone;
      }
    });
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const generateMailtoLink = () => {
    const subject = `Demo Request - ${formData.fullName}${formData.company ? ` from ${formData.company}` : ''}`;
    const body = `Hello,

I would like to request a demo of your telehealth platform.

Contact Information:
- Name: ${formData.fullName}
- Email: ${formData.email}
- Phone: ${formData.phone || 'Not provided'}
- Company/Organization: ${formData.company || 'Not provided'}

${formData.message ? `Additional Message:
${formData.message}` : ''}

Thank you for your time. I look forward to learning more about your platform.

Best regards,
${formData.fullName}`;
    
    return `mailto:${demo.emailRecipient}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    setIsLoading(true);
    
    try {
      // Simulate form processing delay
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      const mailtoLink = generateMailtoLink();
      window.location.href = mailtoLink;
    } catch (error) {
      console.error('Error submitting form:', error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <section className="demo-section" aria-labelledby="demo-title">
      <div className="demo-container">
        <h2 id="demo-title" className="demo-title">
          {demo.title}
        </h2>
        <p className="demo-subtitle">
          {demo.subtitle}
        </p>
        
        <form 
          className="demo-form" 
          onSubmit={handleSubmit}
          aria-label="Demo request form"
          noValidate
        >
          <div className="form-grid">
            {demo.form.fields.map((field) => (
              <FormField
                key={field.name}
                field={field}
                value={formData[field.name]}
                onChange={handleInputChange}
                error={errors[field.name]}
                isLoading={isLoading}
              />
            ))}
          </div>
          
          <LoadingButton
            type="submit"
            className="demo-button"
            isLoading={isLoading}
            loadingText="Submitting..."
            aria-label={isLoading ? 'Submitting demo request' : 'Submit demo request'}
          >
            <EmailIcon style={{ marginRight: '8px' }} aria-hidden="true" />
            {demo.form.buttonText}
          </LoadingButton>
        </form>
      </div>
    </section>
  );
};

export default DemoSection;